import React, { useState } from 'react';
import axios from 'axios';
import { MapPin, Phone, Mail, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const [contactForm, setContactForm] = useState({
    name: '',
    phone: '',
    email: '',
    service: '',
    message: ''
  });

  const [appointmentForm, setAppointmentForm] = useState({
    name: '',
    phone: '',
    email: '',
    service: '',
    preferred_date: '',
    preferred_time: '',
    message: ''
  });

  const [contactStatus, setContactStatus] = useState({ type: '', message: '' });
  const [appointmentStatus, setAppointmentStatus] = useState({ type: '', message: '' });
  const [isContactSubmitting, setIsContactSubmitting] = useState(false);
  const [isAppointmentSubmitting, setIsAppointmentSubmitting] = useState(false);

  const services = [
    'GST Services',
    'Income Tax',
    'Audit & Assurance',
    'Accounting & Compliance',
    'Business Advisory'
  ];

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsContactSubmitting(true);
    setContactStatus({ type: '', message: '' });

    try {
      const response = await axios.post(`${BACKEND_URL}/api/contact`, contactForm);
      setContactStatus({
        type: 'success',
        message: response.data.message
      });
      setContactForm({
        name: '',
        phone: '',
        email: '',
        service: '',
        message: ''
      });
    } catch (error) {
      setContactStatus({
        type: 'error',
        message: error.response?.data?.detail || 'Failed to submit. Please try again.'
      });
    } finally {
      setIsContactSubmitting(false);
    }
  };

  const handleAppointmentSubmit = async (e) => {
    e.preventDefault();
    setIsAppointmentSubmitting(true);
    setAppointmentStatus({ type: '', message: '' });

    try {
      const response = await axios.post(`${BACKEND_URL}/api/appointments`, appointmentForm);
      setAppointmentStatus({
        type: 'success',
        message: response.data.message
      });
      setAppointmentForm({
        name: '',
        phone: '',
        email: '',
        service: '',
        preferred_date: '',
        preferred_time: '',
        message: ''
      });
    } catch (error) {
      setAppointmentStatus({
        type: 'error',
        message: error.response?.data?.detail || 'Failed to book appointment. Please try again.'
      });
    } finally {
      setIsAppointmentSubmitting(false);
    }
  };

  return (
    <div className="contact-page">
      {/* Hero Section */}
      <section className="page-hero">
        <div className="page-hero-overlay"></div>
        <div className="container">
          <h1 className="page-hero-title">Contact Us</h1>
          <p className="page-hero-subtitle">
            Get in touch with us for professional CA services
          </p>
        </div>
      </section>

      {/* Contact Info Section */}
      <section className="contact-info-section">
        <div className="container">
          <div className="contact-info-grid">
            <div className="contact-info-card">
              <div className="contact-info-icon">
                <MapPin size={28} />
              </div>
              <h3 className="contact-info-title">Office Address</h3>
              <p className="contact-info-text">
                Shop No. 3-4, First Floor,<br />
                Gayatri Complex, Ansari Road,<br />
                Muzaffarnagar, Uttar Pradesh - 251001
              </p>
            </div>

            <div className="contact-info-card">
              <div className="contact-info-icon">
                <Phone size={28} />
              </div>
              <h3 className="contact-info-title">Phone</h3>
              <a href="tel:+916396550622" className="contact-info-link">
                +91-6396550622
              </a>
              <a href="tel:+917417215103" className="contact-info-link" style={{ display: 'block', marginTop: '8px' }}>
                +91-7417215103
              </a>
            </div>

            <div className="contact-info-card">
              <div className="contact-info-icon">
                <Mail size={28} />
              </div>
              <h3 className="contact-info-title">Email</h3>
              <a href="mailto:cag.piyush@gmail.com" className="contact-info-link">
                cag.piyush@gmail.com
              </a>
            </div>

            <div className="contact-info-card">
              <div className="contact-info-icon">
                <Clock size={28} />
              </div>
              <h3 className="contact-info-title">Business Hours</h3>
              <p className="contact-info-text">
                Monday - Saturday<br />
                10:00 AM - 6:00 PM
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Forms Section */}
      <section className="contact-forms-section">
        <div className="container">
          <Tabs defaultValue="contact" className="contact-tabs">
            <TabsList className="contact-tabs-list">
              <TabsTrigger value="contact" className="contact-tab">Contact Form</TabsTrigger>
              <TabsTrigger value="appointment" className="contact-tab">Book Appointment</TabsTrigger>
            </TabsList>

            <TabsContent value="contact" className="contact-tab-content">
              <div className="form-container">
                <h2 className="form-title">Send Us a Message</h2>
                <p className="form-subtitle">
                  Fill out the form below and we'll get back to you as soon as possible
                </p>

                <form onSubmit={handleContactSubmit} className="contact-form">
                  <div className="form-row">
                    <div className="form-group">
                      <Label htmlFor="contact-name">Full Name *</Label>
                      <Input
                        id="contact-name"
                        type="text"
                        value={contactForm.name}
                        onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                        required
                        placeholder="Enter your full name"
                      />
                    </div>

                    <div className="form-group">
                      <Label htmlFor="contact-phone">Phone Number *</Label>
                      <Input
                        id="contact-phone"
                        type="tel"
                        value={contactForm.phone}
                        onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                        required
                        placeholder="+91-XXXXXXXXXX"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <Label htmlFor="contact-email">Email Address *</Label>
                      <Input
                        id="contact-email"
                        type="email"
                        value={contactForm.email}
                        onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                        required
                        placeholder="your@email.com"
                      />
                    </div>

                    <div className="form-group">
                      <Label htmlFor="contact-service">Service Required *</Label>
                      <Select 
                        value={contactForm.service}
                        onValueChange={(value) => setContactForm({ ...contactForm, service: value })}
                        required
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select a service" />
                        </SelectTrigger>
                        <SelectContent>
                          {services.map((service) => (
                            <SelectItem key={service} value={service}>
                              {service}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="form-group">
                    <Label htmlFor="contact-message">Message *</Label>
                    <Textarea
                      id="contact-message"
                      value={contactForm.message}
                      onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                      required
                      placeholder="Tell us about your requirements..."
                      rows={5}
                    />
                  </div>

                  {contactStatus.message && (
                    <div className={`form-status ${contactStatus.type}`}>
                      {contactStatus.type === 'success' ? (
                        <CheckCircle size={20} />
                      ) : (
                        <AlertCircle size={20} />
                      )}
                      <span>{contactStatus.message}</span>
                    </div>
                  )}

                  <Button 
                    type="submit" 
                    className="form-submit-btn"
                    disabled={isContactSubmitting}
                  >
                    {isContactSubmitting ? 'Submitting...' : 'Submit & Get Callback'}
                  </Button>
                </form>
              </div>
            </TabsContent>

            <TabsContent value="appointment" className="contact-tab-content">
              <div className="form-container">
                <h2 className="form-title">Book an Appointment</h2>
                <p className="form-subtitle">
                  Schedule a consultation with our expert CA
                </p>

                <form onSubmit={handleAppointmentSubmit} className="contact-form">
                  <div className="form-row">
                    <div className="form-group">
                      <Label htmlFor="appointment-name">Full Name *</Label>
                      <Input
                        id="appointment-name"
                        type="text"
                        value={appointmentForm.name}
                        onChange={(e) => setAppointmentForm({ ...appointmentForm, name: e.target.value })}
                        required
                        placeholder="Enter your full name"
                      />
                    </div>

                    <div className="form-group">
                      <Label htmlFor="appointment-phone">Phone Number *</Label>
                      <Input
                        id="appointment-phone"
                        type="tel"
                        value={appointmentForm.phone}
                        onChange={(e) => setAppointmentForm({ ...appointmentForm, phone: e.target.value })}
                        required
                        placeholder="+91-XXXXXXXXXX"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <Label htmlFor="appointment-email">Email Address *</Label>
                      <Input
                        id="appointment-email"
                        type="email"
                        value={appointmentForm.email}
                        onChange={(e) => setAppointmentForm({ ...appointmentForm, email: e.target.value })}
                        required
                        placeholder="your@email.com"
                      />
                    </div>

                    <div className="form-group">
                      <Label htmlFor="appointment-service">Service *</Label>
                      <Select 
                        value={appointmentForm.service}
                        onValueChange={(value) => setAppointmentForm({ ...appointmentForm, service: value })}
                        required
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select a service" />
                        </SelectTrigger>
                        <SelectContent>
                          {services.map((service) => (
                            <SelectItem key={service} value={service}>
                              {service}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <Label htmlFor="appointment-date">Preferred Date *</Label>
                      <Input
                        id="appointment-date"
                        type="date"
                        value={appointmentForm.preferred_date}
                        onChange={(e) => setAppointmentForm({ ...appointmentForm, preferred_date: e.target.value })}
                        required
                        min={new Date().toISOString().split('T')[0]}
                      />
                    </div>

                    <div className="form-group">
                      <Label htmlFor="appointment-time">Preferred Time *</Label>
                      <Input
                        id="appointment-time"
                        type="time"
                        value={appointmentForm.preferred_time}
                        onChange={(e) => setAppointmentForm({ ...appointmentForm, preferred_time: e.target.value })}
                        required
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <Label htmlFor="appointment-message">Additional Message (Optional)</Label>
                    <Textarea
                      id="appointment-message"
                      value={appointmentForm.message}
                      onChange={(e) => setAppointmentForm({ ...appointmentForm, message: e.target.value })}
                      placeholder="Any specific requirements or queries..."
                      rows={4}
                    />
                  </div>

                  {appointmentStatus.message && (
                    <div className={`form-status ${appointmentStatus.type}`}>
                      {appointmentStatus.type === 'success' ? (
                        <CheckCircle size={20} />
                      ) : (
                        <AlertCircle size={20} />
                      )}
                      <span>{appointmentStatus.message}</span>
                    </div>
                  )}

                  <Button 
                    type="submit" 
                    className="form-submit-btn"
                    disabled={isAppointmentSubmitting}
                  >
                    {isAppointmentSubmitting ? 'Booking...' : 'Book Appointment'}
                  </Button>
                </form>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Map Section */}
      <section className="map-section">
        <div className="container">
          <h2 className="section-title-center">Find Us On Map</h2>
          <p className="map-subtitle">
            Visit our office or get directions to reach us easily
          </p>
          
          <div className="map-container" data-testid="google-map-container">
            <iframe
              title="Office Location - CA Piyush Gupta Muzaffarnagar"
              src="https://www.google.com/maps/embed?pb=!1m12!1m8!1m3!1d55574.80986719203!2d77.7006236!3d29.4747784!3m2!1i1024!2i768!4f13.1!2m1!1sca%20in%20muzaffarnagar!5e0!3m2!1sen!2sin!4v1772264603534!5m2!1sen!2sin"
              width="100%"
              height="450"
              style={{ border: 0, borderRadius: '12px' }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            ></iframe>
            
            <div className="map-directions-btn">
              <a
                href="https://www.google.com/maps/search/?api=1&query=CA+Piyush+Gupta+Gayatri+Complex+Ansari+Road+Muzaffarnagar"
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-primary"
                data-testid="get-directions-btn"
                style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                  <circle cx="12" cy="10" r="3"></circle>
                </svg>
                Get Directions
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
